import type { useGT } from 'gt-react';
import {
  LEAD_TIME_OVERRIDE_DAYS_MAX,
  LEAD_TIME_OVERRIDE_DAYS_MIN,
} from '@erp/shared-types';
import type { LeadTimeOverrideRow } from 'src/api/rpc/hooks/procurement/useLeadTimeOverrides';

// Everything the lead-time override list decides about a row before it renders:
// which status it is in, how it reads, and how it is styled. Kept out of the
// components so the rules are unit-testable without a DOM.

type Translate = ReturnType<typeof useGT>;

export type OverrideStatus =
  'in_effect' | 'shadowed' | 'scheduled' | 'expired' | 'replaced' | 'revoked';

// `overrides` arrives newest first, which is the resolver's last-writer-wins
// order: the first active row is the one that applies, any other active row
// is shadowed until it lapses.
export function statusOf(
  o: LeadTimeOverrideRow,
  active: LeadTimeOverrideRow | null,
  replacedIds: ReadonlySet<string>,
  now: Date,
): OverrideStatus {
  if (o.isActive) return o.id === active?.id ? 'in_effect' : 'shadowed';
  if (o.deactivatedAt) return replacedIds.has(o.id) ? 'replaced' : 'revoked';
  if (new Date(o.startsAt).getTime() > now.getTime()) return 'scheduled';
  return 'expired';
}

export function activeOverrideCount(
  overrides: readonly LeadTimeOverrideRow[],
): number {
  return overrides.filter((o) => o.isActive).length;
}

/** Unrevoked rows whose window has not started: pins waiting for their date. */
export function scheduledOverrideCount(
  overrides: readonly LeadTimeOverrideRow[],
  now: Date,
): number {
  return overrides.filter(
    (o) =>
      !o.isActive &&
      o.deactivatedAt === null &&
      new Date(o.startsAt).getTime() > now.getTime(),
  ).length;
}

/**
 * Whether a row still has an edit / revoke: everything that is not yet out of
 * service. A scheduled pin is one the buyer has not seen apply yet, which is
 * exactly when a mistake gets noticed, so it stays editable and revocable; the
 * backend only refuses rows that were already revoked. Expired rows lapsed on
 * their own and are history.
 */
export function isActionable(status: OverrideStatus): boolean {
  return (
    status === 'in_effect' || status === 'shadowed' || status === 'scheduled'
  );
}

export function parseDays(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
}

export function daysInRange(n: number): boolean {
  return n >= LEAD_TIME_OVERRIDE_DAYS_MIN && n <= LEAD_TIME_OVERRIDE_DAYS_MAX;
}

// Only the row that applies is at full strength; a struck-through row is one
// that was taken out of service rather than one that simply lapsed.
export function daysTextClasses(status: OverrideStatus): string {
  const dim = status === 'in_effect' ? '' : 'text-gray-400';
  const struck =
    status === 'replaced' || status === 'revoked' ? 'line-through' : '';
  return ['font-medium tabular-nums', dim, struck]
    .filter((part) => part !== '')
    .join(' ');
}

export function statusLabel(status: OverrideStatus, gt: Translate): string {
  switch (status) {
    case 'in_effect':
      return gt('Used for ordering now', {
        context: 'lead time override status badge',
      });
    case 'shadowed':
      return gt('Overridden for ordering now', {
        context:
          'lead time override status badge for an active-but-superseded override',
      });
    case 'scheduled':
      return gt('Future eligibility', {
        context: 'lead time override status badge',
      });
    case 'expired':
      return gt('expired', { context: 'lead time override status badge' });
    case 'replaced':
      return gt('replaced', { context: 'lead time override status badge' });
    case 'revoked':
      return gt('revoked', { context: 'lead time override status badge' });
  }
}

export function statusBadgeClasses(status: OverrideStatus): string {
  if (status === 'in_effect') return 'bg-green-100 text-green-700';
  if (status === 'shadowed' || status === 'scheduled') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'text-gray-400';
}
